<ng-container *ngIf="target as task">
    <section class="task-browser__comments" [attr.aria-label]="'Comments for item ' + task.id">
        <button nz-button nzType="link" type="button" class="cb4-button" (click)="toggleComments(task.id)"
            [disabled]="taskWritePending(task.id) || comments.get(task.id)?.editingId != null"
            [attr.aria-expanded]="comments.has(task.id)">
            {{ comments.has(task.id) ? 'Hide comments' : 'Show comments' }}
        </button>
        <div *ngIf="comments.get(task.id) as taskComments" class="task-browser__comment-panel">
            <app-loading-indicator *ngIf="taskComments.loading" message=""></app-loading-indicator>
            <div *ngIf="!taskComments.loading && taskComments.error" class="task-browser__comment-error">
                <span role="alert">{{ taskComments.error }}</span>
                <button nz-button nzType="link" type="button" (click)="retryComments(task.id)">Retry</button>
            </div>
            <p *ngIf="!taskComments.loading && !taskComments.error && !taskComments.items.length">
                {{ taskComments.total ? 'No comments on this page.' : 'No comments yet.' }}
            </p>
            <div class="task-browser__comment-list" *ngIf="!taskComments.loading && !taskComments.error && taskComments.items.length">
                <article *ngFor="let comment of taskComments.items; trackBy: trackComment"
                    class="task-browser__comment">
                    <ng-template #commentMetaTpl>
                    <p class="task-browser__comment-meta">
                        {{ comment.author?.displayName || 'Former user' }} · {{ formatDateTime(comment.createdAt) }}
                        <em *ngIf="comment.updatedAt && comment.updatedAt !== comment.createdAt">
                            · edited {{ formatDateTime(comment.updatedAt) }}
                        </em>
                    </p>
                    </ng-template>
                    <ng-container *ngIf="taskComments.editingId === comment.id; else readCommentTpl">
                        <ng-container *ngTemplateOutlet="commentMetaTpl"></ng-container>
                        <label [attr.for]="'task-comment-edit-' + task.id + '-' + comment.id">Edit comment</label>
                        <textarea nz-input class="task-browser__comment-input" rows="4"
                            [id]="'task-comment-edit-' + task.id + '-' + comment.id"
                            [attr.maxlength]="commentMaxLength"
                            [(ngModel)]="taskComments.editBody"></textarea>
                        <p *ngIf="taskComments.latestBody !== null" class="task-browser__comment-latest">
                            Current saved text: {{ taskComments.latestBody }}
                        </p>
                        <div class="task-browser__comment-actions">
                            <button nz-button nzType="primary" type="button" nzSize="small" class="cb4-button"
                                [disabled]="taskWritePending(task.id) || taskComments.editConflict"
                                (click)="saveEditedComment(task.id)">Save</button>
                            <button nz-button nzType="default" type="button" nzSize="small" class="cb4-button"
                                [disabled]="taskComments.mutating"
                                (click)="cancelEditComment(task.id)">Cancel</button>
                        </div>
                    </ng-container>
                    <ng-template #readCommentTpl>
                        <div class="task-browser__comment-read">
                        <div class="task-browser__comment-text">
                            <ng-container *ngTemplateOutlet="commentMetaTpl"></ng-container>
                            <p class="task-browser__comment-body">{{ comment.body }}</p>
                        </div>
                        <div *ngIf="comment.capabilities.canEditOwnComment || comment.capabilities.canDeleteOwnComment"
                            class="task-browser__comment-actions task-browser__comment-actions--read">
                            <button *ngIf="comment.capabilities.canEditOwnComment" nz-button nzType="text" nzSize="small" class="task-browser__icon-button task-browser__icon-button--edit"
                                type="button" [disabled]="taskWritePending(task.id) || taskComments.editingId !== null"
                                [attr.aria-label]="'Edit comment by ' + (comment.author?.displayName || 'former user')"
                                (click)="startEditComment(task.id, comment)">
                                <i nz-icon nzType="edit" aria-hidden="true"></i>
                            </button>
                            <button *ngIf="comment.capabilities.canDeleteOwnComment" nz-button nzType="text" nzDanger nzSize="small" class="task-browser__icon-button task-browser__icon-button--delete"
                                type="button" [disabled]="taskWritePending(task.id) || taskComments.editingId !== null"
                                [attr.aria-label]="'Delete comment by ' + (comment.author?.displayName || 'former user')"
                                (click)="confirmDeleteComment(task.id, comment)">
                                <i nz-icon nzType="delete" aria-hidden="true"></i>
                            </button>
                        </div>
                        </div>
                    </ng-template>
                </article>
            </div>
            <nz-pagination *ngIf="!taskComments.loading && !taskComments.error && taskComments.total > 5"
                class="task-browser__pagination"
                [nzDisabled]="taskComments.mutating || taskComments.editingId !== null"
                [nzPageIndex]="taskComments.page"
                [nzPageSize]="5"
                [nzTotal]="taskComments.total"
                [nzShowSizeChanger]="false"
                (nzPageIndexChange)="changeCommentPage(task.id, $event)">
            </nz-pagination>
            <div *ngIf="!taskComments.loading && taskComments.editingId !== null && !isEditingCommentVisible(taskComments)"
                class="task-browser__comment-error">
                The comment being edited is no longer on this page. Your draft has not been submitted.
                <button nz-button nzType="link" type="button"
                    (click)="cancelEditComment(task.id)">Discard draft</button>
            </div>
            <div *ngIf="task.capabilities.canAddComment" class="task-browser__comment-compose">
                <label [attr.for]="'task-comment-new-' + task.id">Add new comment</label>
                <textarea nz-input class="task-browser__comment-input" rows="3"
                    [id]="'task-comment-new-' + task.id"
                    [attr.maxlength]="commentMaxLength"
                    [(ngModel)]="taskComments.newBody"></textarea>
                <button nz-button nzType="primary" type="button" nzSize="small" class="cb4-button"
                    [disabled]="taskWritePending(task.id) || taskComments.loading || taskComments.editingId !== null || !taskComments.newBody.trim()"
                    (click)="addComment(task.id)"><i nz-icon nzType="message" aria-hidden="true"></i> Post comment</button>
            </div>
            <div *ngIf="taskComments.mutationError" class="task-browser__comment-error" role="alert">
                {{ taskComments.mutationError }}
                <button *ngIf="taskComments.mutationConflict" nz-button nzType="link"
                    type="button" [disabled]="taskComments.loading"
                    (click)="reloadComments(task.id)">Reload latest</button>
            </div>
        </div>
    </section>
</ng-container>